import { useState } from "react";
import {
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import useOrders, { isNewOrder } from "../orderTable/fetchedOrders";
import { orderStatus } from "./OrderTable";
import { formatDate } from "./formatters";

const notificationKey = ({ order, destination }) =>
  `${destination}-${order.order_id ?? order.id ?? order.tracking_id}`;
function relativeTime(value) {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Time not provided";
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "Just now";
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (seconds < 3600)
    return formatter.format(-Math.floor(seconds / 60), "minute");
  if (seconds < 86400)
    return formatter.format(-Math.floor(seconds / 3600), "hour");
  return formatter.format(-Math.floor(seconds / 86400), "day");
}
export default function OrderNotifications({ role, onSelect }) {
  const [allOpen, setAllOpen] = useState(false);
  let account = role;
  try {
    const admin = JSON.parse(localStorage.getItem("adminData") || "{}");
    account = (admin.data || admin).aid ?? role;
  } catch {}
  const storageKey = `readOrderNotifications:${account}`;
  const [read, setRead] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "{}");
    } catch {
      return {};
    }
  });
  const [anchor, setAnchor] = useState(null);
  const { data = [], isLoading, isError } = useOrders();
  const canManage = ["super_admin", "admin", "cashier"].includes(role);
  const canSeeKitchen = canManage || role === "kitchen";
  const notifications = (Array.isArray(data) ? data : [])
    .flatMap((order) => {
      if (canSeeKitchen && orderStatus(order) === "accepted")
        return [
          {
            order,
            destination: "kitchen",
            title: "Order sent to kitchen",
            description: "Accepted and waiting for preparation",
          },
        ];
      if (canManage && isNewOrder(order))
        return [
          {
            order,
            destination: "orders",
            title: "New online order",
            description: "Review and accept or cancel this order",
          },
        ];
      return [];
    })
    .sort(
      (a, b) =>
        (Date.parse(b.order.created_at) || 0) -
        (Date.parse(a.order.created_at) || 0)
    );
  const unreadCount = notifications.filter(
    (notification) => !read[notificationKey(notification)]
  ).length;
  const markRead = (keys) =>
    setRead((current) => {
      const next = { ...current };
      keys.forEach((key) => {
        next[key] = true;
      });
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}
      return next;
    });
  const markAll = () => markRead(notifications.map(notificationKey));
  const renderList = (entries) =>
    isLoading ? (
      <Box sx={{ p: 3 }}>Loading notifications…</Box>
    ) : isError ? (
      <Box sx={{ p: 3 }}>Unable to load notifications.</Box>
    ) : !entries.length ? (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          No new order notifications.
        </Typography>
      </Box>
    ) : (
      <List disablePadding>
        {entries.map((notification) => {
          const { order, title, description, destination } = notification;
          const key = notificationKey(notification);
          const seen = Boolean(read[key]);
          const Icon =
            destination === "kitchen"
              ? RestaurantOutlinedIcon
              : ShoppingBagOutlinedIcon;
          return (
            <ListItemButton
              key={key}
              onClick={() => {
                markRead([key]);
                setAnchor(null);
                setAllOpen(false);
                onSelect?.(destination);
              }}
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2.5,
                gap: 2,
                alignItems: "flex-start",
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: seen ? "background.paper" : "#f8fcfa",
                "&:hover": { bgcolor: "#eef8f3" },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 42, sm: 52 },
                  height: { xs: 42, sm: 52 },
                  bgcolor: "#def6e9",
                  color: "#20a66a",
                  mt: 0.25,
                }}
              >
                <Icon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#24433b",
                    fontWeight: seen ? 500 : 700,
                    overflowWrap: "anywhere",
                  }}
                >
                  {title} · {order.tracking_id || order.order_id || order.id}
                </Typography>
                <Typography variant="body2" sx={{ color: "#7893a1", mt: 0.75 }}>
                  {description}
                </Typography>
                <Tooltip
                  title={
                    order.created_at
                      ? formatDate(order.created_at, true)
                      : "Time not provided"
                  }
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ display: "inline-block", color: "#8ba2ae", mt: 1 }}
                  >
                    {relativeTime(order.created_at)}
                  </Typography>
                </Tooltip>
              </Box>
              <Stack spacing={2} alignItems="flex-end">
                <Chip
                  size="small"
                  label={seen ? "Read" : "New"}
                  sx={{
                    minWidth: 56,
                    height: 23,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: seen ? "#f1f4f5" : "#def6e9",
                    color: seen ? "#7c909b" : "#20a66a",
                  }}
                />
                <ChevronRightIcon sx={{ color: "#8ba2ae", fontSize: 22 }} />
              </Stack>
            </ListItemButton>
          );
        })}
      </List>
    );
  const heading = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <NotificationsOutlinedIcon sx={{ color: "#ed8b00" }} />
        <Typography variant="h6" fontWeight={700} sx={{ color: "#24433b" }}>
          Notifications
        </Typography>
      </Stack>
      <Button
        onClick={markAll}
        disabled={!unreadCount}
        sx={{ color: "#ed8b00", textTransform: "none", fontSize: 13 }}
      >
        Mark all as read
      </Button>
    </Stack>
  );
  if (!canManage && !canSeeKitchen) return null;
  return (
    <>
      <Tooltip title="Order notifications">
        <IconButton
          aria-label="Order notifications"
          aria-expanded={Boolean(anchor)}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <Badge color="error" badgeContent={unreadCount}>
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorReference="anchorPosition"
        anchorPosition={
          anchor
            ? {
                top:
                  (anchor.closest("header")?.getBoundingClientRect().bottom ??
                    anchor.getBoundingClientRect().bottom) + 8,
                left: anchor.ownerDocument.documentElement.clientWidth - 12,
              }
            : undefined
        }
        onClose={() => setAnchor(null)}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          role: "region",
          "aria-label": "Order notifications",
          sx: {
            width: 520,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "80vh",
            borderRadius: 4,
            right: { xs: 12, sm: 24 },
            left: "auto !important",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 12px 40px rgba(20,46,41,.2)",
          },
        }}
      >
        {heading}
        <Divider />
        <Box sx={{ overflowY: "auto", minHeight: 0 }}>
          {renderList(notifications.slice(0, 4))}
        </Box>
        <Box sx={{ p: 1.5, textAlign: "center" }}>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              setAnchor(null);
              setAllOpen(true);
            }}
            sx={{ color: "#ed8b00", textTransform: "none", fontWeight: 600 }}
          >
            View All Notifications
          </Button>
        </Box>
      </Popover>
      <Dialog
        open={allOpen}
        onClose={() => setAllOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="all-notifications-title"
      >
        <DialogTitle id="all-notifications-title">
          All Notifications
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {heading}
          <Divider />
          {renderList(notifications)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
